import { useEffect, useId, useRef } from 'react'
import { createPortal } from 'react-dom'
import Button from './Button'
import useBodyScrollLock from './useBodyScrollLock'
import useExitTransition from './useExitTransition'
import { kit } from './strings'
import './ResultDialog.css'

/**
 * The outcome dialog — the app's ONE answer to "did that work?".
 *
 * The legacy confirms every write with a centred SweetAlert (`Swal.fire({text,
 * icon})`, 355 of them across the MVC views): a coloured mark, the server's
 * sentence, and an `إغلاق` you have to press. That interruption is the point —
 * a write is not finished until the person who asked for it has read the
 * answer — so it is kept, and only the surface is rebuilt.
 *
 * Rebuilt in the SHEET's language, the same way ConfirmDialog was: a tone chip
 * beside a heading, the message ranged to the reading edge, the button in a
 * footer band. What SweetAlert did with a poster — everything centred under a
 * floating disc — this does with the app's own rhythm, so an outcome reads as
 * part of the product rather than as a library that wandered in.
 *
 * The mark draws itself: the ring sweeps, then the glyph is stroked on. That
 * half second is the whole reason the dialog does not feel like a blocker —
 * something happened, and the dialog is showing you it happening.
 *
 * @param {{result: boolean, message: string}|null} result  legacy shape: `result` picks the tone
 * @param {'success'|'danger'|'info'} [tone]  overrides the boolean when a third tone is needed
 */
export default function ResultDialog({ result, tone, closeLabel = kit.close, onClose }) {
  const messageId = useId()
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  const { mounted, state, ref } = useExitTransition(!!result)
  const closeRef = useRef(null)

  /* The last outcome, kept so the dialog still has something to SAY while it
     animates out. `result` is already null by then — without this the box
     would fade away empty. */
  const shown = useRef(null)
  if (result) shown.current = { result, tone }
  const current = result ? { result, tone } : shown.current

  useBodyScrollLock(!!result)

  useEffect(() => {
    if (!result) return undefined

    /* `preventScroll` because the dialog is portalled to <body> while the page
       behind is still scrolled where the reader left it. */
    closeRef.current?.focus({ preventScroll: true })

    const onKeyDown = (event) => {
      if (event.key === 'Escape') onCloseRef.current()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [result])

  if (!mounted || !current) return null

  const key = current.tone ?? (current.result.result ? 'success' : 'danger')

  /* To <body> — same reason as ConfirmDialog: a fixed overlay must not be
     anchored by whatever the page column happens to be doing. */
  return createPortal(
    <div
      className={`outcome outcome--${key}${state === 'leaving' ? ' is-leaving' : ''}`}
      data-testid="result-dialog"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby={messageId}
      /* The scrim dismisses, like SweetAlert's. The button stays the primary
         way out; this only spares a reach for a message already read. */
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
      {...(state === 'leaving' ? { inert: true, 'aria-hidden': 'true' } : null)}
    >
      <div className="outcome__box" ref={ref}>
        {/* Centred, on one axis. A chip beside a line of text is a form's
            header row — the shape the eye reads as "a section starts here",
            not as "here is the answer". An outcome has exactly one thing to
            say, and the middle of the box is where the eye already is when the
            dialog opens.

            No heading above the message either: every outcome string in the app
            is already the whole sentence (`تمت العملية بنجاح`), so a status
            heading over it printed the same words twice. */}
        <div className="outcome__body">
          {/* Keyed on the sentence, so a SECOND outcome arriving while this one
              is open re-runs the mark's draw instead of swapping the text under
              a stroke that has already finished. That used to be a `key` on the
              whole dialog up in ToastProvider, which remounted the component
              and left the exit animation nothing to play on. */}
          <OutcomeMark key={current.result.message} tone={key} />
          <p id={messageId} className="outcome__message" data-testid="result-message">
            <bdi>{current.result.message}</bdi>
          </p>
        </div>
        <div className="outcome__actions">
          {/* Focused in an effect, NOT with the `autoFocus` attribute. The
              attribute is treated as a non-pointer interaction, so the button
              paints `:focus-visible` the instant the dialog opens and every
              outcome arrived wearing a ring. A programmatic `.focus()` follows
              the browser's own modality heuristic instead: keyboard users still
              see the ring, a click does not summon one. */}
          <Button
            ref={closeRef}
            variant={key === 'danger' ? 'danger' : 'primary'}
            onClick={onClose}
            data-testid="result-close"
          >
            {closeLabel}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

/**
 * The mark — pressed, like a stamp.
 *
 * It arrives a little too big and settles, and a single ring is left behind
 * expanding out of it. That is the ONE idea in this dialog, and it is one
 * gesture: something came down and landed. Everything else — the box, the
 * sentence, the button — just eases in together, because a dialog with seven
 * things animating is a dialog you watch instead of read.
 */
const GLYPHS = {
  success: ['M14 22.6l5.4 5.4L30.2 16.4'],
  danger: ['M15.6 15.6l12.8 12.8', 'M28.4 15.6L15.6 28.4'],
  info: ['M22 20.4v9.6'],
}

function OutcomeMark({ tone }) {
  return (
    <span className="outcome__mark" aria-hidden="true">
      {/* The ring the press leaves behind. An element rather than a
          pseudo-element, so the disc keeps its own background. */}
      <i className="outcome__halo" />
      <svg viewBox="0 0 44 44" role="presentation">
        <circle className="outcome__ring" cx="22" cy="22" r="19" />
        {GLYPHS[tone].map((d) => (
          <path key={d} d={d} />
        ))}
        {tone === 'info' && <circle className="outcome__dot" cx="22" cy="15" r="1.5" />}
      </svg>
    </span>
  )
}
